import React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import { useTheme, alpha } from "@mui/material/styles";

import EmailIcon from "@mui/icons-material/Email";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function Support() {
    const theme = useTheme();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}15 100%)`,
            }}
        >
            {/* Navigation Bar */}
            <Box
                sx={{
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <Typography
                            variant="h5"
                            component={RouterLink}
                            to="/"
                            sx={{
                                fontWeight: 800,
                                color: theme.palette.primary.main,
                                textDecoration: "none",
                                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                            }}
                        >
                            TapSheet
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            startIcon={<ArrowBackIcon />}
                            variant="outlined"
                            size="small"
                            sx={{
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                px: { xs: 1.5, sm: 2 },
                            }}
                        >
                            Back
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 3 } }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                mb: { xs: 1.5, sm: 2 },
                                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                            }}
                        >
                            Support
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ 
                                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" }, 
                                lineHeight: 1.7 
                            }}
                        >
                            We're here to help! Get in touch with us through
                            any of the options below.
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Email Support Card */}
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: alpha(theme.palette.common.black, 0.08),
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: { xs: "flex-start", sm: "flex-start" },
                                    gap: { xs: 1.5, sm: 2 },
                                }}
                            >
                                <Box
                                    sx={{
                                        color: theme.palette.primary.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <EmailIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                                </Box>
                                <Box sx={{ flex: 1, width: "100%" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 1,
                                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                        }}
                                    >
                                        Email Support
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ 
                                            mb: 2, 
                                            lineHeight: 1.7,
                                            fontSize: { xs: "0.9rem", sm: "1rem" },
                                        }}
                                    >
                                        For immediate assistance, questions, or
                                        feedback, please reach out to us
                                        directly via email.
                                    </Typography>
                                    <Box
                                        component="a"
                                        href="mailto:snapsheetsocials@gmail.com"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            color: theme.palette.primary.main,
                                            textDecoration: "none",
                                            fontWeight: 600,
                                            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.1rem" },
                                            px: { xs: 2, sm: 3 },
                                            py: { xs: 1, sm: 1.5 },
                                            borderRadius: 1,
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.1,
                                            ),
                                            transition: "all 0.2s ease-in-out",
                                            width: { xs: "100%", sm: "auto" },
                                            justifyContent: { xs: "center", sm: "flex-start" },
                                            "&:hover": {
                                                backgroundColor: alpha(
                                                    theme.palette.primary.main,
                                                    0.15,
                                                ),
                                                textDecoration: "none",
                                            },
                                        }}
                                    >
                                        <EmailIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                        <Box
                                            component="span"
                                            sx={{
                                                wordBreak: { xs: "break-all", sm: "normal" },
                                                textAlign: { xs: "center", sm: "left" },
                                            }}
                                        >
                                            snapsheetsocials@gmail.com
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Help Resources Card */}
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: alpha(theme.palette.common.black, 0.08),
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: { xs: "flex-start", sm: "flex-start" },
                                    gap: { xs: 1.5, sm: 2 },
                                }}
                            >
                                <Box
                                    sx={{
                                        color: theme.palette.primary.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <HelpOutlineIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                                </Box>
                                <Box sx={{ flex: 1, width: "100%" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 1,
                                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                        }}
                                    >
                                        Help Resources
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ 
                                            mb: 2, 
                                            lineHeight: 1.7,
                                            fontSize: { xs: "0.9rem", sm: "1rem" },
                                        }}
                                    >
                                        Check out our documentation and legal
                                        pages for more information.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Button
                                            component={RouterLink}
                                            to="/privacy"
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                justifyContent: { xs: "center", sm: "flex-start" },
                                                textTransform: "none",
                                                py: { xs: 1.25, sm: 1.5 },
                                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                            }}
                                        >
                                            Privacy Policy
                                        </Button>
                                        <Button
                                            component={RouterLink}
                                            to="/terms"
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                justifyContent: { xs: "center", sm: "flex-start" },
                                                textTransform: "none",
                                                py: { xs: 1.25, sm: 1.5 },
                                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                            }}
                                        >
                                            Terms of Service
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Container>

            {/* Footer */}
            <Box
                sx={{
                    py: 3,
                    px: { xs: 2, sm: 4 },
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "background.paper",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/privacy"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                                "&:hover": {
                                    color: "text.primary",
                                },
                            }}
                        >
                            Privacy Policy
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/terms"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                                "&:hover": {
                                    color: "text.primary",
                                },
                            }}
                        >
                            Terms of Service
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/support"
                            variant="text"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                textTransform: "none",
                                "&:hover": {
                                    color: "text.primary",
                                },
                            }}
                        >
                            Support
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
